import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { RndResizeCallback } from 'react-rnd'
import { Grid } from '@mui/material'

import { useUpdateWindowMutation } from '@redux/api/windows.api'

import { ResizeGridItemWrapper } from '@features/ResizeGridItemWrapper'

import { getElementPathByDroppableId, getLineById } from '@helpers'
import { GRID_SIZE } from '@constants'
import { WindowConfigContainerType, WindowConfigElement } from '@types'

import { Field } from './Field'
import { WindowConfigContext } from './WindowConfig'

type DraggableFieldOnSectionProps = {
  element: WindowConfigElement
  index: number
  countSizeRow: number
  pathToLine: string
  width: number
}

export const DraggableFieldOnSection: FC<DraggableFieldOnSectionProps> = ({
  element,
  index,
  pathToLine,
  countSizeRow,
  width,
}) => {
  const { containers, windowById, onTransformConfigsToPOST } = useContext(WindowConfigContext)
  const [updateWindow, { isLoading: isLoadingUpdateWindow }] = useUpdateWindowMutation()

  const [currentSize, setCurrentSize] = useState(element.size)
  const [textTitle, setTextTitle] = useState('')

  const isSmallSize = currentSize === 1
  const elementId = `elementId:${element.id}`

  const newItemRef = useRef<HTMLDivElement>(null)

  const minWidth = useMemo(() => width / GRID_SIZE, [width])

  useEffect(() => {
    if (element?.title) {
      setTextTitle(element?.title)
    }
  }, [])

  const handleDeleteFieldOnForm = () => {
    const newContainers = JSON.parse(JSON.stringify(containers)) as WindowConfigContainerType[]
    const [containerId, tabId, lineId] = getElementPathByDroppableId(pathToLine)
    const { line } = getLineById(newContainers, containerId, tabId, lineId)

    if (!line) {
      return
    }

    line.elements.splice(index, 1)

    const configs = onTransformConfigsToPOST(newContainers)

    if (configs) {
      updateWindow({
        ...windowById,
        configs,
      })
    }
  }

  const getSize = (width: number): number => {
    const widthSize = Math.round(width / minWidth)
    const threshold = GRID_SIZE * 0.75 // Порог 9 ячеек (75% от 12)

    // Если текущий размер 12
    if (currentSize === GRID_SIZE) {
      return widthSize < threshold ? GRID_SIZE / 2 : GRID_SIZE
    }
    // Если текущий размер 6
    else {
      return widthSize > threshold ? GRID_SIZE : GRID_SIZE / 2
    }
  }

  const handleResize: RndResizeCallback = (e, direction, ref, delta) => {
    const result = getSize(parseInt(ref.style.width, 10))

    setCurrentSize(result)
  }

  const handleResizeStop: RndResizeCallback = (e, direction, ref, delta) => {
    const [, , lineId] = getElementPathByDroppableId(pathToLine)

    const result = getSize(parseInt(ref.style.width, 10))

    const newContainers = JSON.parse(JSON.stringify(containers)) as WindowConfigContainerType[]
    const configs = onTransformConfigsToPOST(newContainers).map(el => {
      if (el.line === lineId && el.column === element.column) {
        return {
          ...el,
          size: result,
        }
      }

      return el
    })

    if (result !== element.size) {
      updateWindow({
        ...windowById,
        configs,
      })
    }
  }

  return (
    <>
      <Draggable draggableId={elementId} index={index}>
        {(provided, snapshot) => {
          const dragHandlePropsForContainer =
            isSmallSize && provided.dragHandleProps ? provided.dragHandleProps : undefined
          const dragHandlePropsForIcon =
            !isSmallSize && provided.dragHandleProps ? provided.dragHandleProps : undefined

          return (
            <Grid
              ref={provided.innerRef}
              item
              alignItems={'center'}
              {...provided.draggableProps}
              sx={{
                py: 0.5,
              }}
            >
              <ResizeGridItemWrapper
                countSize={countSizeRow}
                minWidthByGrid={GRID_SIZE / 2}
                size={currentSize}
                width={width}
                onResize={handleResize}
                onResizeStop={handleResizeStop}
              >
                <Field
                  ref={newItemRef}
                  dragHandlePropsForContainer={dragHandlePropsForContainer}
                  dragHandlePropsForIcon={dragHandlePropsForIcon}
                  elementId={element?.id}
                  elementTitle={element?.title}
                  handleDeleteFieldOnForm={handleDeleteFieldOnForm}
                  // isEditOrPopup={textElementCreationInProgress}
                  isSmallSize={isSmallSize}
                  textTitle={textTitle}
                />
              </ResizeGridItemWrapper>
            </Grid>
          )
        }}
      </Draggable>
    </>
  )
}
